"use client";
import { cn } from "@gettactic/helpers/src/css/cn";
import { getDeviceTZ } from "@gettactic/helpers/src/intl/getDeviceTZ";
import { useAuthenticatedUser, useDedicatedExceptions } from "@gettactic/hooks";
import { formatInTimeZone } from "date-fns-tz";
import { CalendarClock } from "lucide-react";
import React from "react";
import {
  describeRelease,
  formatReleaseSpan,
  upcomingReleases
} from "@/features/map/helpers/dedicatedReleases";

export interface DedicatedReleasesProps {
  resourceId: string;
  /** False keeps the request from firing while the panel is hidden. */
  enabled?: boolean;
  className?: string;
  /**
   * Shown under the heading when no release is ahead. Without it the whole
   * block stays out of the way, which is what a map popover wants.
   */
  emptyLabel?: string;
  /**
   * True for resource managers. The desk's own owner is recognised from the
   * releases themselves, so callers need not work out whose desk it is.
   * Everyone else sees which days the desk can be booked, but not the reason
   * its owner gave for being away, nor the names of the people it was handed
   * to. A co-owner of a shared workspace is 'everyone else' for the other seats.
   */
  canSeeDetails?: boolean;
  /**
   * True on a workspace shared by several owners, where each release frees
   * one owner's seat: every release then says whose seat it is.
   */
  showsSeatOwner?: boolean;
  /** The office's zone, so "already over" is judged on its calendar. */
  timeZone?: string;
}

/**
 * The days a dedicated desk is released to others while its owner is away,
 * for the desk's popover on the map, the reserve card and the dashboard's
 * desk details: the owner sees what they gave away, everyone else sees when
 * the desk can be booked.
 */
export const DedicatedReleases: React.FC<DedicatedReleasesProps> = ({
  resourceId,
  enabled = true,
  className,
  emptyLabel,
  canSeeDetails = false,
  showsSeatOwner = false,
  timeZone
}) => {
  const { data: releases, isLoading } = useDedicatedExceptions(resourceId, {
    enabled
  });
  const authenticatedUser = useAuthenticatedUser();
  // Every release names its seat's owner, redacted or not, so an owner is
  // recognised on any day, and on a shared workspace only on their own
  // seat's releases. The resource's `dedicated_exceptions` only list the
  // releases active on the day it was looked at.
  const viewerId = authenticatedUser.user?.id;
  const upcoming = React.useMemo(
    () =>
      upcomingReleases(
        releases ?? [],
        formatInTimeZone(
          new Date(),
          timeZone || getDeviceTZ() || "UTC",
          "yyyy-MM-dd"
        )
      ),
    [releases, timeZone]
  );

  if (upcoming.length === 0 && (!emptyLabel || isLoading)) {
    return null;
  }

  return (
    <div className={cn("mt-3", className)}>
      <h5 className="font-semibold text-[#8b949b] text-[11px] uppercase tracking-[0.06em]">
        Released to others
      </h5>
      {upcoming.length === 0 && (
        <p className="mt-1.5 text-neutral-500 text-sm">{emptyLabel}</p>
      )}
      <ul className="mt-1.5 flex flex-col gap-1.5">
        {upcoming.map((release) => {
          const showsDetails =
            canSeeDetails ||
            (!!viewerId && release.dedicated_user_id === viewerId);
          const seatOwner =
            showsSeatOwner && release.dedicated_user?.name
              ? `${release.dedicated_user.name}'s seat · `
              : "";
          return (
            <li
              key={release.id}
              className="flex items-center gap-3 rounded-[10px] border-[0.5px] border-[rgba(20,29,38,0.1)] bg-[#fbfcfd] px-[13px] py-[11px]"
            >
              <CalendarClock
                className="h-4 w-4 shrink-0 text-neutral-400"
                aria-hidden="true"
              />
              <div className="min-w-0">
                <p className="truncate font-inter font-semibold text-[14px] text-neutral-900">
                  {formatReleaseSpan(release.starts_on, release.ends_on)}
                </p>
                <p className="truncate font-inter text-[#778086] text-[12px]">
                  {seatOwner}
                  {showsDetails
                    ? describeRelease(release)
                    : release.release === "open"
                      ? "Anyone"
                      : "Specific people"}
                  {showsDetails && release.reason ? ` · ${release.reason}` : ""}
                </p>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
